import type { SelectedTrain } from "~/features/train-info/schemas";
import { getMilliSecondsUntilTimeOfDay } from "~/utils/date";

import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import type { DefaultOptions, UseQueryOptions } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { toMilliseconds } from "duration-fns";
import localforage from "localforage";

type StaticQueryOptions = Pick<
  UseQueryOptions,
  "queryKey" | "gcTime" | "staleTime"
>;

export const QUERY_CLIENT_PERSISTER = createAsyncStoragePersister({
  storage: localforage,
});

export const DEFAULT_QUERY_OPTIONS = {
  queries: {
    gcTime: toMilliseconds({
      weeks: 1,
    }),
    staleTime: toMilliseconds({
      minutes: 10,
    }),
  },
} satisfies DefaultOptions;

export const dayPlanQuery = (employeeNumber?: string, overrideDate?: string) =>
  ({
    queryKey: ["day-plans", employeeNumber, overrideDate] as const,
    gcTime: toMilliseconds({
      weeks: 1,
    }),
    staleTime: toMilliseconds({
      minutes: 30,
    }),
  }) satisfies StaticQueryOptions;

export const circularKeys = {
  all: () => ["circulars"] as const,
  acknowledge: () => ["circulars", "acknowledge"] as const,
};

export const CIRCULARS_QUERY = {
  queryKey: circularKeys.all(),
  staleTime: toMilliseconds({ minutes: 20 }),
  gcTime: toMilliseconds({ weeks: 1 }),
} satisfies StaticQueryOptions;

export const departureQuery = (train: SelectedTrain) => {
  return {
    queryKey: [
      "departure",
      {
        date:
          train?.nominalDate &&
          formatISO(train.nominalDate, { representation: "date" }),
        trainId: train?.trainId,
      },
    ] as const,
    staleTime: toMilliseconds({ minutes: 20 }),
    gcTime: toMilliseconds({ weeks: 1 }),
  } satisfies StaticQueryOptions;
};

// FIDO Reserve documents are updated at 3 AM UTC (6 AM Oslo time) daily
// Cache until then to ensure all users get fresh data at the same time
export const fidoDocumentsQuery = () => {
  return {
    queryKey: ["documents", "sharepoint-train_operation-fido_reserve"],
    staleTime: getMilliSecondsUntilTimeOfDay({ hour: 6 }),
    gcTime: toMilliseconds({ days: 2 }),
  } satisfies StaticQueryOptions;
};

export const sharepointDocumentsQuery = () => {
  return {
    queryKey: ["documents", "sharepoint-train_operation-documents"] as const,
    staleTime: 0,
    gcTime: toMilliseconds({ days: 2 }),
  } satisfies StaticQueryOptions;
};

export const linkKeys = {
  all: () => ["links"] as const,
};

export const favoriteKeys = {
  markAsFavorite: () => ["favorites", "markAsFavorite"],
};

export const stationInformationQuery = (trainId?: string) => {
  return {
    queryKey: ["station-information", trainId] as const,
    gcTime: toMilliseconds({
      hours: 6,
    }),
    staleTime: toMilliseconds({
      seconds: 20, //station information gives us departing and arriving trains, with estimated and actual departure/arrival times which can change frequently
    }),
  } satisfies StaticQueryOptions;
};
